
// src/lib/bookingsApi.js
import { db } from "../firebase";
import {

    collection,
    doc,
    onSnapshot,
    query,
    where,
    runTransaction,
    serverTimestamp,
    getDoc,

} from "firebase/firestore";

/**
 * Central bookings collection you moved to:
 * services/bookings/bookinglist
 */
export const centralBookingsCol = collection(db, "services", "bookings", "bookinglist");

/** Subscribe bookings by category (for employee dashboard) */
export const subscribeBookingsByCategory = (categoryKey, onData, onError) => {
    if (!categoryKey) {
        onError?.(new Error("categoryKey required"));
        return () => { };
    }
    const qRef = query(centralBookingsCol, where("category", "==", categoryKey));
    return onSnapshot(
        qRef,
        (snap) => {
            const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            items.sort((a, b) => (b.requestedAt?.seconds || 0) - (a.requestedAt?.seconds || 0));
            onData(items);
        },
        (err) => onError?.(err)
    );
};

export const subscribeOpenJobsByCategory = (
    { categoryKey, locationKey }, // locationKey optional
    onData,
    onError
) => {
    try {
        if (!categoryKey) {
            onError?.(new Error("categoryKey is required"));
            return () => { };
        }
        // Build query: category == categoryKey AND (assignedToUid == null) AND (workerStatus != 'completed')
        // We’ll query minimum indexes; if you set workerStatus, prefer equality filters.
        let qRef = query(centralBookingsCol, where("category", "==", categoryKey));
        // If you store locationKey on booking docs, enable this:
        if (locationKey && locationKey !== "all") {
            qRef = query(qRef, where("locationKey", "==", locationKey));
        }
        return onSnapshot(
            qRef,
            (snap) => {
                const items = snap.docs
                    .map((d) => ({ id: d.id, ...d.data() }))
                    .filter((b) => !b.assignedToUid && (b.workerStatus || "open") !== "completed");
                // newest first
                items.sort((a, b) => (b.requestedAt?.seconds || 0) - (a.requestedAt?.seconds || 0));
                onData(items);
            },
            (err) => onError?.(err)
        );
    } catch (e) {
        onError?.(e);
        return () => { };
    }
};

/** Subscribe the current employee’s ACTIVE job (claimed/in-progress and not completed) */
export const subscribeMyActiveJob = (uid, onData, onError) => {
    try {
        if (!uid) {
            onError?.(new Error("uid is required"));
            return () => { };
        }
        // We filter client-side after pulling category scope because Firestore needs simple queries.
        const unsub = onSnapshot(
            centralBookingsCol,
            (snap) => {
                const mine = snap.docs
                    .map((d) => ({ id: d.id, ...d.data() }))
                    .find(
                        (b) =>
                            b.assignedToUid === uid &&
                            (b.workerStatus || "open") !== "completed"
                    );
                onData(mine || null);
            },
            (err) => onError?.(err)
        );
        return unsub;
    } catch (e) {
        onError?.(e);
        return () => { };
    }
};

/**
 * Transaction: claim a job
 * - Only succeeds if assignedToUid is null (unclaimed)
 * - Optional: ensure worker has no active job (pass hasActiveJob=true to block)
 */
export const claimJobTransaction = async (bookingId, employeeUid, { blockIfActiveJob = true } = {}) => {
    if (!bookingId || !employeeUid) throw new Error("bookingId and employeeUid are required");
    const ref = doc(centralBookingsCol, bookingId);

    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Booking not found");
        const data = snap.data();

        // Already assigned?
        if (data.assignedToUid) throw new Error("This job was already claimed by another employee.");

        // Optional: you can enforce that this employee has no active job
        // by checking a parallel doc or pre-check outside. Here we rely on UI to prevent.
        // If you want hard enforcement, you can maintain a separate "activeJob" doc per employee.

        tx.update(ref, {
            assignedToUid: employeeUid,
            assignedAt: serverTimestamp(),
            workerStatus: "claimed",
            updatedAt: serverTimestamp(),
        });
    });
};

/**
 * Transaction: mark job as completed
 * - Only the assigned employee can complete
 */
export const completeJobTransaction = async (bookingId, employeeUid) => {
    if (!bookingId || !employeeUid) throw new Error("bookingId and employeeUid are required");
    const ref = doc(centralBookingsCol, bookingId);

    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Booking not found");
        const data = snap.data();

        if (data.assignedToUid !== employeeUid) {
            throw new Error("You are not assigned to this job.");
        }
        tx.update(ref, {
            workerStatus: "completed",
            completedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
    });
};

/** Get a single booking doc (for checks) */
export const getBookingOnce = async (bookingId) => {
    const ref = doc(centralBookingsCol, bookingId);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};